//@ts-check

/**
 * @file JSDoc type definitions for Product Combination
 */

/**
 * @typedef {'SEGMENT_LIST' | 'SEGMENT_FOLDER'} SegmentSelectionType
 * Type of segment configuration selected from UI
 */

/**
 * @typedef {Object} SegmentDetails
 * Details of a segment
 * @property {string} [segmentId] - Segment IDs
 * @property {string} [segmentName] - Segment name
 * @property {string} [segmentFolderId] - Segment folder ID
 */

/**
 * @typedef {Object} SegmentConfiguration
 * Configuration for customer segments
 * @property {SegmentSelectionType} [selectionType='SEGMENT_LIST'] - Type of segment configuration selected from UI
 * @property {SegmentDetails[]} segments - Selected segments within same group
 */

/**
 * @typedef {Object} SegmentConfigurationWrapper
 * Segment configuration for the combo product
 * If the top-level allCustomers flag is true, then includedSegments is not required
 * Mandatory if the top-level allCustomers flag is false
 * @property {SegmentConfiguration[] | null} [includedSegments] - Included segments for the combo product
 * @property {SegmentConfiguration | null} [excludedSegment] - Excluded segment for the combo product
 */

/**
 * @typedef {Object.<string, string>} LanguageStringMap
 * Map of language codes to string values
 */

/**
 * @typedef {'DRAFT' | 'PUBLISHED' | 'ACTIVE' | string} ProductComboStatus
 * Backend status values returned for a combo detail.
 */

/**
 * @typedef {Object} ProductComboSku
 * Product assigned to the combo.
 * @property {string} productId - Product identifier.
 * @property {number} quantity - Product quantity within the combo.
 * @property {string} [productName] - Product name.
 * @property {string} [size] - Product size label.
 * @property {string} [displayCategory] - Product display category.
 * @property {string} [displayBrand] - Product display brand.
 * @property {string} [status] - Product status.
 */

/**
 * @typedef {Object} ProductListingItem
 * Product row returned by the product listing API.
 * @property {string} productId - Product identifier.
 * @property {string} status - Product status.
 * @property {string} productName - Product name.
 * @property {string|number} sorting - Sort order value returned by the API.
 * @property {'TRUE'|'FALSE'|boolean|null} [showOnECommerce] - Raw e-commerce visibility value from the API response.
 * @property {boolean|null} [showOnEcommerce] - Normalized e-commerce visibility used by the UI.
 * @property {number|string|null} suggestedProductSellingPrice - Suggested selling price.
 * @property {number|string|null} multipleSellingUnits - Multiple selling units value.
 * @property {string} displayName_en - English display name.
 * @property {string} displayName_locale - Locale-specific display name.
 * @property {number} subCategoryCount - Number of subcategories linked to the product.
 * @property {number} segmentCount - Number of segments linked to the product.
 * @property {string} gtin - Global Trade Item Number.
 * @property {string} brand - Brand name or identifier.
 * @property {string} category - Product category.
 * @property {string} [subCategory_en] - English subcategory label when present.
 * @property {number| null} quantity - Quantity of the product within the combo.
 */

/**
 * @typedef {Object} ProductComboDisplayCategory
 * @property {string} en - English display category label.
 * @property {string} [es_MX] - Spanish (Mexico) display category label.
 */

/**
 * @typedef {Object} ProductComboSubCategory
 * Subcategory linked to the combo.
 * @property {string} code - Subcategory identifier expected by the backend.
 * @property {LanguageStringMap} values - Subcategory names keyed by language code.
 */

/**
 * @typedef {Object} ProductCombo
 * Product Combination form data.
 * @property {string} name - Combo name. Mandatory.
 * @property {string} productId - Combo id. ID created by backend when submitting a draft or publishing
 * @property {string} [salesOrg] - Sales organization code from the combo detail response.
 * @property {string} [countryCode] - Country code from the combo detail response.
 * @property {string} [imageUrl] - Product combo image URL.
 * @property {number|null} comboLimit - Maximum number of times a user can use the combo product.
 * @property {number|null} productLimit - Maximum number of products allowed in the combo.
 * @property {string[]} [displayCategoryIds] - Display categories linked to the combo.
 * @property {ProductComboDisplayCategory[]} [displayCategories] - Selected display category objects linked to the combo.
 * @property {ProductComboSubCategory[]} [subCategories] - Optional subcategories linked to the combo.
 * @property {ProductComboSku[]} [comboSkus] - Products linked to the combo.
 * @property {LanguageStringMap} [comboSubtitle] - Subtitle text keyed by language code.
 * @property {LanguageStringMap} [comboDetails] - Details text keyed by language code.
 * @property {LanguageStringMap} [comboDetails2] - Additional details text keyed by language code.
 * @property {LanguageStringMap} [comboDescription] - Description text keyed by language code.
 * @property {LanguageStringMap} [keywords] - Keywords keyed by language code.
 * @property {string|null} [createdDateTime] - Server-generated combo creation timestamp in ISO format.
 * @property {string|null} [updatedDateTime] - Server-generated combo last update timestamp in ISO format.
 * @property {ProductComboStatus} [status] - Backend workflow status for the combo.
 * @property {string|null} [updatedBy] - User identifier that last updated the combo.
 * @property {string} routeTypeId - Selected route type ID from the single-select dropdown. Mandatory.
 * @property {string} [businessGroup] - Business group identifier ('SNK' for Snacks or 'BVG' for Beverages).
 * @property {boolean} [active=true] - Whether the product combination is available.
 * @property {boolean} [allCustomers=true] - Whether all customers are eligible for the combo product.
 * @property {boolean} [excludeSegment=false] - Whether selected segments are excluded from the combo product.
 * @property {boolean} [isDraft=true] - Whether the product combination is a draft.
 * @property {boolean} [ignoreSkuList=true] - Whether to ignore the SKU list when validating the combo.
 * @property {SegmentConfigurationWrapper} [segmentConfiguration] - Segment configuration selected from the multi-select dropdown.
 * @property {'pepsi'} scope - Whether the combo is for pepssi-wide or customer-specific use. Defaults to 'pepsi'.
 */

/**
 * @typedef {Object} ProductComboDetailPayload
 * Raw combo detail payload returned by the backend.
 * @property {string} productId - Combo identifier.
 * @property {string} [salesOrg] - Sales organization code.
 * @property {string} [scope] - Scope associated with the combo.
 * @property {string} [countryCode] - Country code.
 * @property {string} [name] - Combo name.
 * @property {number|null} [comboLimit] - Combo usage limit.
 * @property {boolean} [active] - Availability flag.
 * @property {boolean} [allCustomers] - Whether the combo applies to all customers.
 * @property {SegmentConfigurationWrapper} [segmentConfiguration] - Segment configuration from the backend.
 * @property {boolean} [excludeSegment] - Whether the selected segments are excluded.
 * @property {number|null} [productLimit] - Maximum products allowed in the combo.
 * @property {LanguageStringMap} [comboSubtitle] - Normalized subtitle map.
 * @property {LanguageStringMap} [comboDetails] - Normalized details map.
 * @property {LanguageStringMap} [comboDetails2] - Normalized secondary details map.
 * @property {LanguageStringMap} [comboDescription] - Normalized description map.
 * @property {LanguageStringMap} [keywords] - Keywords keyed by language code.
 * @property {boolean} [ignoreSkuList] - Whether SKU validation is bypassed.
 * @property {ProductComboSku[]} [comboSkus] - Rich combo SKU objects.
 * @property {ProductComboSubCategory[]} [subCategories] - Subcategories linked to the combo.
 * @property {ProductComboStatus} [status] - Workflow status.
 * @property {string|null} [createdDatetime] - Backend creation timestamp.
 * @property {string|null} [updatedDatetime] - Backend update timestamp.
 * @property {string|null} [updatedBy] - User that last updated the combo.
 * @property {boolean} [isDraft] - Draft flag.
 * @property {string} [externalId] - Legacy combo identifier field.
 * @property {LanguageStringMap} [shortDescription] - Legacy subtitle map.
 * @property {LanguageStringMap} [longDescription] - Legacy details map.
 * @property {LanguageStringMap} [comboLongDescription2] - Legacy secondary details map.
 * @property {LanguageStringMap} [productDescription] - Legacy description map.
 * @property {string|null} [createdDateTime] - Legacy creation timestamp.
 * @property {string|null} [updatedDateTime] - Legacy update timestamp.
 */

/**
 * @typedef {Object} ProductComboDetailApiResponse
 * Combo detail API envelope.
 * @property {number} statusCode - HTTP-style response status code.
 * @property {boolean} success - Whether the request succeeded.
 * @property {string} [message] - Response message.
 * @property {ProductComboDetailPayload|null} [data] - Combo detail payload.
 * @property {string|null} [errorCode] - Backend error code.
 * @property {unknown} [domainContext] - Backend domain context.
 * @property {string|null} [timestamp] - Response timestamp.
 */

/**
 * Product Combination wizard steps.
 * @type {{SETUP: number, DISPLAY: number, PRODUCTS: number, REVIEW: number}}
 */
export const ProductComboSteps = Object.freeze({
  SETUP: 1,
  DISPLAY: 2,
  PRODUCTS: 3,
  REVIEW: 4,
})

/**
 * Reusable empty Product Combination state.
 * @type {ProductCombo}
 */
export const emptyProductCombo = Object.freeze({
  name: '',
  productId: '',
  imageUrl: '',
  comboLimit: null,
  productLimit: null,
  displayCategoryIds: [],
  displayCategories: [],
  subCategories: [],
  comboSkus: [],
  comboSubtitle: {},
  comboDetails: {},
  comboDetails2: {},
  comboDescription: {},
  keywords: {},
  routeTypeId: '',
  businessGroup: '',
  active: true,
  isDraft: true,
  allCustomers: true,
  excludeSegment: false,
  ignoreSkuList: true,
  segmentConfiguration: {
    includedSegments: null,
    excludedSegment: null,
  },
  scope: 'pepsi',
})

// export const emptyProductCombo = Object.freeze({
//   name: 'husfaiuhfpuashfsaifhasiufha',
//   productId: '',
//   imageUrl: 'usfhoiajfpisafa',
//   comboLimit: 4,
//   productLimit: 5,
//   displayCategoryIds: [],
//   displayCategories: [],
//   comboSkus: [],
//   comboSubtitle: {en: 'Subtitle in English', es_MX: 'Subtítulo en Español'},
//   comboDetails: {en: 'Details in English', es_MX: 'Detalles en Español'},
//   comboDetails2: {en: 'Additional details in English', es_MX: 'Detalles adicionales en Español'},
//   comboDescription: {en: 'Description in English', es_MX: 'Descripción en Español'},
//   routeTypeId: '1',
//   active: true,
//   allCustomers: true,
//   excludeSegment: false,
//   ignoreSkuList: true,
//   segmentConfiguration: {
//     includedSegments: null,
//     excludedSegment: null,
//   },
//   scope: 'pepsi',
// })
